import {
  buffers, eventChannel,
} from 'redux-saga';
import {
  call, delay, put, race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import {
  type AreaQueryEventData, type AreaQueryFailedEventData, type AreaQuerySuccessEventData, registerAreaQueryBroadcastListener,
} from '~/_shared/utils/api/broadcast.helpers';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import type { PickAction } from '~/_shared/utils/types/action.type';
import {
  getMapBoundaryAreaQueryAsyncResult,
  getMapBoundaryAreaQueryAsyncStart,
  type MapBoundaryAreaQueryStartAsyncResponse,
  MapQueryStatusResponse,
} from '~/store/boundaryItems/boundaryItems.repository';
import type { ProcessingAction } from '~/store/frontendState/processing/processing.action';
import {
  processingExportCreate,
  processingExportRemove,
  processingExportSetError,
  processingExportSetPageSuccessResults,
} from '~/store/frontendState/processing/processing.actionCreators';
import { fileExportsSelector } from '~/store/frontendState/processing/processing.selectors';
import {
  type FileExportId, FileExportType,
} from '~/store/frontendState/processing/processing.state';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '~/store/userData/userData.actionTypes';

const POLLING_DELAY = 20_000;

type FileExportEventData = Readonly<{
  hash: string;
  error?: string;
}> & (
  Readonly<{ type: FileExportType.BoundaryAreaQuery; data: AreaQueryEventData }>
);

const itemChannelEmitters = new Map<string, Array<(message: FileExportEventData) => void>>();

export function* processingExportSagas() {
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, initializeWebSocketChannel);
  yield takeEvery(processingExportCreate, onProcessingExportAdded);
}

function* initializeWebSocketChannel() {
  const sharedChannel = registerAreaQueryListener();

  try {
    yield call(broadcastMessagesToItemChannels, sharedChannel);
  }
  finally {
    if (sharedChannel) {
      sharedChannel.close();
    }
  }
}

function* broadcastMessagesToItemChannels(sharedChannel: ReturnType<typeof registerAreaQueryListener>) {
  while (true) {
    if (!sharedChannel) {
      break;
    }

    const message: FileExportEventData = yield take(sharedChannel);

    const hash = message.hash;
    if (!hash) {
      continue;
    }

    const emitters = itemChannelEmitters.get(hash);
    if (emitters) {
      emitters.forEach(emitter => emitter(message));
    }
  }
}

function* onProcessingExportAdded(action: PickAction<ProcessingAction, typeof processingExportCreate.type>) {
  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);

  if (!clientId) {
    return;
  }

  let hash: string | undefined;

  if (action.payload.fileExport.type === FileExportType.BoundaryAreaQuery) {
    try {
      const response: MapBoundaryAreaQueryStartAsyncResponse
        = yield call(getMapBoundaryAreaQueryAsyncStart, clientId, action.payload.fileExport.request);
      hash = response?.hash;
    }
    catch (e) {
      console.error('Failed to area query export', e);
    }
  }

  if (hash) {
    yield call(watchExportProgressSaga, {
      fileExportId: action.payload.fileExport.id,
      hash,
      type: action.payload.fileExport.type,
    });
  }
}

function* watchExportProgressSaga({
  fileExportId,
  hash,
  type,
}: {
  fileExportId: FileExportId;
  hash: string;
  type: FileExportType;
}) {
  const itemChannel = registerExportItemChannel(hash);

  if (!itemChannel) {
    return;
  }

  // Guard against cancellation that happened before this saga started
  // (e.g. remove was dispatched during the async start API call)
  const currentExports: ReturnType<typeof fileExportsSelector> = yield select(fileExportsSelector);
  if (!currentExports.some(e => e.id === fileExportId)) {
    return;
  }

  try {
    while (true) {
      const { message, cancelled }: {
        message: FileExportEventData | undefined;
        cancelled: Action | undefined;
      } = yield race({
        message: take(itemChannel),
        timeout: delay(POLLING_DELAY),
        cancelled: take((action: Action) =>
          action.type === processingExportRemove.type && action.payload.id === fileExportId
        ),
      });

      if (cancelled) {
        break;
      }

      if (message) {
        if (type === FileExportType.BoundaryAreaQuery) {
          if (message.data?.message !== MapQueryStatusResponse.Finished) {
            // WS event still in progress — keep waiting
            continue;
          }
          if (message.error) {
            yield put(processingExportSetError(fileExportId, 'An error occurred during processing'));
            break;
          }
        }
      }

      // Either WS Finished event or polling timeout — fetch the actual result
      const clientId: number | null = yield select<number | null>(clientIdSelector);

      if (clientId === null) {
        break;
      }

      try {
        let fileUrl: string | undefined;
        let data: string | undefined;

        if (type === FileExportType.BoundaryAreaQuery) {
          const response: Awaited<ReturnType<typeof getMapBoundaryAreaQueryAsyncResult>>
            = yield call(getMapBoundaryAreaQueryAsyncResult, clientId, hash);

          fileUrl = 'download' in response ? response.download || undefined : undefined;
          data = 'data' in response ? response.data || undefined : undefined;
        }

        if (fileUrl || data) {
          if (fileUrl) {
            downloadFile(fileUrl);
          }
          yield put(processingExportSetPageSuccessResults(fileExportId, 0, { fileUrl, data }));
          break;
        }

        if (message) {
          // WS said Finished but result has no URL — treat as error
          yield put(processingExportSetError(fileExportId, 'Export result not available'));
          break;
        }
        // Polling timeout with no URL yet — continue waiting
      }
      catch {
        yield put(processingExportSetError(fileExportId, 'Failed to fetch export results'));
        break;
      }
    }
  }
  finally {
    itemChannel.close();
  }
}

const registerAreaQueryListener = () => {
  return eventChannel<FileExportEventData>(emitter => {
    registerAreaQueryBroadcastListener(
      (e: AreaQuerySuccessEventData) => {
        return emitter({
          hash: e.hash,
          type: FileExportType.BoundaryAreaQuery,
          data: e,
        });
      },
      (e: AreaQueryFailedEventData) => {
        return emitter({
          hash: e.hash,
          type: FileExportType.BoundaryAreaQuery,
          data: e,
          error: e.reason,
        });
      }
    );

    return noop;
  }, buffers.expanding());
};

const registerExportItemChannel = (hash: string) => {
  return eventChannel<FileExportEventData>((emitterFn) => {
    if (!itemChannelEmitters.has(hash)) {
      itemChannelEmitters.set(hash, []);
    }
    const emitters = itemChannelEmitters.get(hash);
    if (emitters) {
      emitters.push(emitterFn);
    }

    return () => {
      const currentEmitters = itemChannelEmitters.get(hash);
      if (currentEmitters) {
        const index = currentEmitters.indexOf(emitterFn);
        if (index > -1) {
          currentEmitters.splice(index, 1);
        }
        if (currentEmitters.length === 0) {
          itemChannelEmitters.delete(hash);
        }
      }
    };
  }, buffers.expanding());
};
